import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import moment from "moment";
import { staffSubmitOnBehalfApi } from "src/DAL/tracker/trackerSubmission";
import { isPersonalInfoLibraryId } from "./trackerStorage";
import {
  buildJourneySchedule,
  findCheckInSchedulePoint,
  formatScheduleStepLabel,
} from "./journeyScheduleHelpers";

const MUTED = "#64748b";
const INK = "#0f172a";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const BORDER = "#e2e8f0";
const inputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" },
};

const STATE_META = {
  done: { label: "Done", color: "#047857", bg: "rgba(16,185,129,0.12)" },
  current: { label: "Open now", color: "#0369a1", bg: "rgba(14,165,233,0.14)" },
  missed: { label: "Missed", color: "#b45309", bg: "rgba(245,158,11,0.14)" },
  locked: { label: "Locked", color: "#b91c1c", bg: "rgba(239,68,68,0.12)" },
  upcoming: { label: "Later", color: MUTED, bg: "#f1f5f9" },
};

// schedulePoint (findCheckInSchedulePoint) adds this check-in's extra
// questions and drops the shared ones it excludes.
const questionsOfForm = (tracker, form, schedulePoint = null) => {
  if (!tracker || !form) return [];
  const excluded = new Set(schedulePoint?.excludedSharedQuestionIds || []);
  const shared = (
    Array.isArray(tracker.sharedQuestions) ? tracker.sharedQuestions : []
  ).filter((q) => !excluded.has(String(q?.id || "")));
  const personal = Array.isArray(tracker.personalInfoQuestions)
    ? tracker.personalInfoQuestions
    : [];
  const formQs = Array.isArray(form.questions) ? form.questions : [];
  const useShared = form.useSharedQuestions !== false;
  const pointExtras = (schedulePoint?.extraQuestions || []).map((q) => ({
    ...q,
    fieldType: q.fieldType || "short_text",
    required: Boolean(q.required),
  }));
  const merged = [
    ...personal,
    ...(useShared ? shared : []),
    ...formQs,
    ...pointExtras,
  ];
  const seen = new Set();
  return merged.filter((q) => {
    const id = String(q?.id || q?.questionId || "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    const lib = String(q?.libraryQuestionId || q?.questionId || "");
    if (isPersonalInfoLibraryId?.(lib)) return false;
    if (
      /first.?name|last.?name|email|phone/i.test(String(q?.label || "")) &&
      (q?.role === "personal" || q?.group === "personal")
    ) {
      return false;
    }
    return true;
  });
};

/**
 * Coach/admin fills a check-in for a member — same schedule dates as the
 * member portal; only open (or missed) dates can be selected.
 */
export default function StaffCheckInDialog({
  open,
  tracker,
  person,
  personSubmissions = [],
  onClose,
  onSaved,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [stepId, setStepId] = useState("");
  const [answers, setAnswers] = useState({});
  const [triggerAutomation, setTriggerAutomation] = useState(false);
  const [saving, setSaving] = useState(false);

  const schedule = useMemo(
    () => buildJourneySchedule(tracker, personSubmissions || []),
    [tracker, personSubmissions]
  );

  const formsById = useMemo(() => {
    const map = new Map();
    (Array.isArray(tracker?.forms) ? tracker.forms : []).forEach((f) => {
      if (f?.id) map.set(String(f.id), f);
    });
    return map;
  }, [tracker]);

  const selectableSteps = useMemo(() => {
    if (!schedule?.steps?.length) return [];
    return schedule.steps.filter(
      (s) => s.kind === "check_in" && s.selectable
    );
  }, [schedule]);

  useEffect(() => {
    if (!open) return;
    const preferred =
      selectableSteps.find((s) => s.state === "current") ||
      selectableSteps[0] ||
      null;
    setStepId(preferred?.id || "");
    setAnswers({});
    setTriggerAutomation(false);
    setSaving(false);
  }, [open, person?.key, selectableSteps]);

  const selectedStep =
    schedule?.steps?.find((s) => s.id === stepId) ||
    selectableSteps.find((s) => s.id === stepId) ||
    null;
  const form = selectedStep
    ? formsById.get(String(selectedStep.formId)) || null
    : null;
  // The check-in is saved today, so today's schedule point applies.
  const schedulePoint = useMemo(
    () =>
      selectedStep?.kind === "check_in"
        ? findCheckInSchedulePoint(tracker, personSubmissions || [])
        : null,
    [tracker, personSubmissions, selectedStep]
  );
  const questions = useMemo(
    () => questionsOfForm(tracker, form, schedulePoint),
    [tracker, form, schedulePoint]
  );

  const setAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSave = async () => {
    if (!tracker?.id && !tracker?._id) return;
    if (!person) {
      enqueueSnackbar("Pick a member first", { variant: "warning" });
      return;
    }
    if (!selectedStep || !selectedStep.selectable) {
      enqueueSnackbar("Pick an open check-in date", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const res = await staffSubmitOnBehalfApi({
        trackerId: tracker.id || tracker._id,
        memberId: person.memberId || "",
        personKey: person.personKey || person.key || "",
        email: person.email || "",
        formId: selectedStep.formId || "",
        answers,
        triggerAutomation,
      });
      if (res?.code !== 200) {
        const when = res?.unlockAt
          ? ` (opens ${String(res.unlockAt).slice(0, 10)})`
          : "";
        enqueueSnackbar(
          `${res?.message || "Could not save check-in"}${when}`,
          { variant: "error" }
        );
        return;
      }
      enqueueSnackbar(res.message || "Check-in saved", { variant: "success" });
      onSaved?.(res.submission);
      onClose?.();
    } catch (_err) {
      enqueueSnackbar("Could not save check-in", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: INK, pb: 1 }}>
        Check-in for member
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: MUTED, mb: 2 }}>
          {person?.name || "Member"}
          {person?.email ? ` · ${person.email}` : ""}
          . Same dates as the member portal — pick an open check-in.
          {triggerAutomation
            ? " This week's progress report will be sent on save."
            : " Leave Send report off to rely on the automation schedule."}
        </Typography>

        {schedule?.steps?.length ? (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 700, color: INK, mb: 1 }}
            >
              Check-in dates
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {schedule.steps.map((step) => {
                const meta = STATE_META[step.state] || STATE_META.upcoming;
                const selected = step.id === stepId;
                const canPick = step.selectable;
                return (
                  <Box
                    key={step.id}
                    component="button"
                    type="button"
                    disabled={!canPick || saving}
                    onClick={() => {
                      if (!canPick) return;
                      setStepId(step.id);
                      setAnswers({});
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      width: "100%",
                      textAlign: "left",
                      p: 1.25,
                      borderRadius: "10px",
                      border: `1px solid ${
                        selected ? PRIMARY : BORDER
                      }`,
                      bgcolor: selected
                        ? "color-mix(in srgb, var(--portal-theme-primary) 8%, white)"
                        : "#fff",
                      cursor: canPick ? "pointer" : "default",
                      opacity: canPick || step.state === "done" ? 1 : 0.55,
                      fontFamily: "inherit",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: INK,
                          lineHeight: 1.3,
                        }}
                      >
                        {formatScheduleStepLabel(step)}
                      </Typography>
                      {step.kind === "check_in" && step.opensAt ? (
                        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.2 }}>
                          {moment(step.opensAt).format("ddd, D MMM YYYY")}
                        </Typography>
                      ) : null}
                    </Box>
                    <Chip
                      size="small"
                      label={meta.label}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        fontSize: 11,
                        bgcolor: meta.bg,
                        color: meta.color,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
            {!selectableSteps.length ? (
              <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 1 }}>
                No open check-in right now — wait until the next date unlocks.
              </Typography>
            ) : null}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
            This journey has no repeating check-in schedule.
          </Typography>
        )}

        {selectedStep && form ? (
          questions.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              No questions on this form yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }}>
                Answers for {formatScheduleStepLabel(selectedStep)}
              </Typography>
              {questions.map((q) => {
                const id = String(q.id || q.questionId || "");
                const multiline =
                  q.fieldType === "long_text" || q.type === "long_text";
                return (
                  <TextField
                    key={id}
                    fullWidth
                    size="small"
                    label={q.label || "Question"}
                    required={Boolean(q.required || q.isRequired)}
                    multiline={multiline}
                    minRows={multiline ? 3 : undefined}
                    value={answers[id] ?? ""}
                    onChange={(e) => setAnswer(id, e.target.value)}
                    sx={inputSx}
                  />
                );
              })}
            </Box>
          )
        ) : null}

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Switch
              checked={triggerAutomation}
              onChange={(e) => setTriggerAutomation(e.target.checked)}
              size="small"
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: 13, color: INK }}>
                Send report
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, lineHeight: 1.35 }}>
                On: send this week's progress report now. Off: only the
                automation send-on weeks fire the report.
              </Typography>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={onClose}
          disabled={saving}
          style={{ padding: "8px 16px" }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="small-contained-button"
          onClick={handleSave}
          disabled={saving || !selectedStep?.selectable}
          style={{ padding: "8px 16px", opacity: saving ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Save check-in"}
        </button>
      </DialogActions>
    </Dialog>
  );
}
